import {Trans} from '@lingui/macro';
import React, {ReactNode, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Dropdown} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemCheckbox} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemCheckbox';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownList} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {SearchDropdownHeader} from '../../../components/common/dropdowns/search/searchDropdownHeader';
import {fuzzySearchBuilder} from '../../../helpers/fuzzySearchHelpers';
import {ChannelModel} from '../../../models/api/channelModel';
import {SignaturesChannelDropdownContent} from './signaturesChannelDropdownContent';

/*
 * Constant.
 */

const dropdownDimensions = {
  width: 300, // px
  maxHeight: 400, // px
};

/*
 * Props.
 */

/** Represents a toggle input for "All team channels" or "All individual channels". */
export interface SignatureChannelDropdownInputItem {
  onChange: Handler;
  label: ReactNode;
  isSelected: boolean;
  key: string;
}

export interface SignaturesChannelDropdownInheritableProps {
  inputItems: ReadonlyArray<SignatureChannelDropdownInputItem>;
  selectedItems: ReadonlyArray<ChannelModel> | null;
  items: ReadonlyArray<ChannelModel>;
  getItemKey: (item: ChannelModel) => string;
  renderItemLabel: (item: ChannelModel) => ReactNode;
  renderItemIcon?: (item: ChannelModel) => ReactNode;
}

export type SignaturesChannelDropdownProps = SignaturesChannelDropdownInheritableProps & {
  popoverProps: PopoverTransferProps;
  onChange: HandlerOf<ReadonlyArray<ChannelModel>>;
  serializeItemForSearch: (item: ChannelModel) => string;
};

/*
 * Style.
 */

const StyledAllChannelItemsContainer = styled.div`
  margin-bottom: 2px;
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
  padding-bottom: 5px;
`;

/*
 * Component.
 */

export const SignaturesChannelDropdown = (props: SignaturesChannelDropdownProps) => {
  const {popoverProps, selectedItems, inputItems, onChange} = props;
  const [searchQuery, setSearchQuery] = useState('');

  const handleChange = (item: ChannelModel) => {
    if (selectedItems?.includes(item)) {
      onChange(selectedItems.filter((i) => i !== item));
    } else {
      onChange([...(selectedItems || []), item]);
    }
  };

  const searchedItems = useMemo(() => {
    if (searchQuery.trim().length < 1) {
      return props.items;
    }

    const fuzzySearch = fuzzySearchBuilder(props.serializeItemForSearch);
    return fuzzySearch(props.items, searchQuery);
  }, [searchQuery, props.items, props.serializeItemForSearch]);

  return (
    <Dropdown {...popoverProps} width={dropdownDimensions.width} maxHeight={dropdownDimensions.maxHeight}>
      <SearchDropdownHeader
        title={<Trans id="signatures-channel-dropdown-title">Channel access</Trans>}
        value={searchQuery}
        onChange={setSearchQuery}
        shouldIncludeSearchIcon
      />
      <DropdownList>
        {renderAllChannelInputItems(inputItems)}
        {searchedItems.length ? (
          <SignaturesChannelDropdownContent
            selectedItems={selectedItems}
            onChange={handleChange}
            items={searchedItems}
            getItemKey={props.getItemKey}
            renderItemLabel={props.renderItemLabel}
            renderItemIcon={props.renderItemIcon}
            serializeItemForSearch={props.serializeItemForSearch}
          />
        ) : null}
      </DropdownList>
    </Dropdown>
  );
};

/*
 * Helpers.
 */

function renderAllChannelInputItems(inputItems: ReadonlyArray<SignatureChannelDropdownInputItem>) {
  return (
    <StyledAllChannelItemsContainer>
      {inputItems.map((item) => (
        <DropdownItem
          key={item.key}
          renderRightContent={() => <DropdownItemCheckbox isChecked={item.isSelected} />}
          isChecked={item.isSelected}
          onSelect={item.onChange}
        >
          <DropdownItemContent>{item.label}</DropdownItemContent>
        </DropdownItem>
      ))}
    </StyledAllChannelItemsContainer>
  );
}
